import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {CompanyService} from '../../generated/api/company.service';
import {UserService} from '../../generated/api/user.service';
import {AuthenticationService} from '../../authenticationModule/services/authentication.service';
import {UserLoggedRolesServiceService} from '../../authenticationModule/services/user-logged-roles-service.service';
import {PageCrud} from '../../Enum/page-crud.enum';
import {CompanyLabel} from '../../Enum/company-label.enum';
import {ClientUtilitiesService} from '../../services/client-utilities.service';
import {CustomCompanyService} from '../../services/custom-company.service';
import {ConfirmationService, Message, SelectItem} from 'primeng/api';
import {UtilsService} from '../../appSharedModule/services/utils.service';
import {Company} from '../../models/company';
import {DadCompanySetting} from '../../models/DadCompanySetting';

@Component({
  selector: 'app-client-crud',
  templateUrl: './client-crud.component.html',
  styleUrls: ['./client-crud.component.scss']
})
export class ClientCrudComponent implements OnInit {

  page: PageCrud;
  submitted = false;
  loading = false;
  error = false;
  existingEmail = false;
  successAdd = false;
  alertNoChange = false;
  accessDenied = false;
  successEdit = false;
  currentCompany: Company;
  companyUuid = '';
  affiliationMckay = false;
  affiliationQuincy = false;
  affiliationVendor = false;
  primaryContactName = '';
  primaryContactEmail = '';
  companyAdminUsers = [];
  clientUsers = [];
  updateClient = {};
  newClientData = {};
  primaryContact = {};
  dadConfigsNames: string[] = [];
  changedDadSettings = [];
  dadConfigSaved = false;
  msgsAws: Message[] = [];
  billingAccounts: any;
  errorMessage: string;
  successDelete = false;
  salesforceAccounts = [];
  salesforceAccountsDropdown: SelectItem[] = [];
  selectedSalesforceAccount: any;
  accountDropdownPlaceHolder = 'Loading Accounts...';
  companyUniqid: string;
  newAction: string;
  action: string;
  selectedSalesforceAccountName: string;
  isTimeSyncOnly = false;

  constructor(private _route: ActivatedRoute,
              private confirmationService: ConfirmationService,
              private authenticationService: AuthenticationService,
              private userService: UserService,
              private roleService: UserLoggedRolesServiceService,
              private companyService: CompanyService,
              private clientUtilitiesService: ClientUtilitiesService,
              private customCompanyService: CustomCompanyService,
              private utilsService: UtilsService
  ) {
  }

  get hasMultipleBusinessUnits() {
    if (!this.currentCompany.billingAccounts) {
      return false;
    }
    return this.currentCompany.billingAccounts.length > 1;
  }

  ngOnInit() {
    this.loading = true;
    this.utilsService.lockUi();
    this._route.paramMap.subscribe(params => {
      const currentCompanyUniqId = params.get('currentCompanyUniqId');
      if (currentCompanyUniqId !== this.getUserCurrent().company.uniqid) {
        this.redirectionToNotFound();
        return;
      }
      this.initDadConfigs();
      this.companyUniqid = params.get('companyUniqId');
      this.action = params.get('action');
      this.newAction = params.get('newAction');
      if (!!this.companyUniqid && this.newAction !== 'new') {
        this.getClientByUniqid(this.companyUniqid);
        if (this.action === 'edit' && this.roleService.userHasOneOfThoseRoles(this.getUserCurrent(), ['ROLE_MCKAY_SUPER', 'ROLE_MCKAY_ADMIN'])) {
          this.page = PageCrud.EDIT;
          this.utilsService.unlockUi();
          this.getCompanyAdminUsers();
        } else if (this.action === 'company') {
          this.page = PageCrud.SHOW;
          this.utilsService.unlockUi();
        } else {
          this.accessDenied = true;
          this.utilsService.unlockUi();
          this.loading = false;
        }
      } else {
        this.getCompanyAdminUsers();
        this.page = PageCrud.ADD;
        this.utilsService.unlockUi();
        this.loading = false;
      }
      this.initSalesforceAccounts(this.companyUniqid);
    });
  }

  getUserCurrent() {
    return this.authenticationService.getUserCurrent();
  }

  redirectionToNotFound() {
    location.assign('/#/404');
  }

  onSubmit(): void {
    this.submitted = true;
    this.error = false;
    this.existingEmail = false;
    if (!this.isValidRegisterForm()) {
      return;
    }
    if (this.page === PageCrud.ADD) {
      this.addClient();
    }
    if (this.page === PageCrud.EDIT) {
      this.editClient();
    }
  }

  isValidRegisterForm() {
    return this.selectedSalesforceAccount
      && this.primaryContactName !== ''
      && ((this.page === PageCrud.ADD
        && this.primaryContactEmail !== ''
        && this.validateEmail(this.primaryContactEmail)
        && (this.affiliationMckay || this.affiliationQuincy || this.affiliationVendor)) || this.page === PageCrud.EDIT);
  }

  hasAffiliation(): boolean {
    return this.affiliationMckay || this.affiliationQuincy || this.affiliationVendor;
  }

  canAddOrEditClients(): boolean {
    return this.roleService.userHasOneOfThoseRoles(this.getUserCurrent(), ['ROLE_MCKAY_SUPER', 'ROLE_MCKAY_ADMIN']);
  }

  canShowClients(): boolean {
    return this.roleService.userHasOneOfThoseRoles(this.getUserCurrent(), ['ROLE_MCKAY']);
  }

  hasPermission(): boolean {
    return (this.page === PageCrud.EDIT || this.page === PageCrud.ADD) && this.canAddOrEditClients()
      || this.page === PageCrud.SHOW && this.canShowClients();
  }

  errorAddClient(error) {
    this.successAdd = false;
    this.error = false;
    this.existingEmail = false;
    if (error.status === 409 && error.error.includes('same name already exists')) {
      this.error = true;
      this.errorMessage = 'There is a client with the same name.';
    } else if (error.status === 409 && error.error.includes('User with the same email')) {
      this.existingEmail = true;
      this.error = true;
      this.errorMessage = 'There is a user with this email address.';
    } else {
      this.error = true;
      this.errorMessage = 'An error occurred while processing your request.';
    }
    return;
  }

  editClient() {
    if (this.currentCompany.sugarUuid
      && this.selectedSalesforceAccount
      && this.selectedSalesforceAccount !== this.currentCompany.sugarUuid
      && this.salesforceAccounts.filter(account => account.uuid === this.currentCompany.sugarUuid).length > 0) {
      this.confirmationService.confirm({
        message: 'Are you sure that you want to modify the matching account from '
          + this.salesforceAccounts.filter(account => account.uuid === this.currentCompany.sugarUuid)[0]['name'] + ' to '
          + this.salesforceAccounts.filter(account => account.uuid === this.selectedSalesforceAccount)[0]['name'] + '? Please confirm.',
        accept: () => {
          this.updateCompanyInfo();
        },
        key: 'salesforceAccountCD'
      });
    } else {
      this.updateCompanyInfo();
    }
  }


  getPrimaryContact() {
    const primaryContact = this.clientUsers.find(user => user.name === this.primaryContactName);
    if (primaryContact) {
      return primaryContact.id;
    }
    return '';
  }

  parseClientFromMckayAndOrQuincy(company = this.currentCompany): string {
    if (!company) {
      return '';
    }
    let companyType = '';
    if (company.label === CompanyLabel.LABEL_MCKAY) {
      companyType = 'Mckay';
    } else if (company.label === CompanyLabel.LABEL_QUINCY) {
      companyType = 'Quincy';
    } else if (company.label === CompanyLabel.LABEL_ALL) {
      companyType = 'Mckay and Quincy';
    } else if (company.label === CompanyLabel.LABEL_VENDOR) {
      return 'Vendor';
    }
    return 'Client from ' + companyType;
  }

  isEditPage() {
    return this.page === PageCrud.EDIT;
  }

  isAddPage() {
    return this.page === PageCrud.ADD;
  }

  isShowPage() {
    return this.page === PageCrud.SHOW;
  }

  validateEmail(primaryContactEmail: string) {
    // tslint:disable-next-line:max-line-length
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(primaryContactEmail).toLowerCase());
  }

  check(event: string) {
    if ((event === 'mckay' && !this.affiliationMckay && this.affiliationVendor)
      || (event === 'quincy' && !this.affiliationQuincy && this.affiliationVendor)) {
      this.affiliationVendor = false;
    }
    if (event === 'vendor' && !this.affiliationVendor) {
      if (this.affiliationMckay) {
        this.affiliationMckay = false;
      }
      if (this.affiliationQuincy) {
        this.affiliationQuincy = false;
      }
    }
  }

  editDadCompanySetting(dadCompanySetting: DadCompanySetting) {
    if (dadCompanySetting.dadConfig.exchangeNameInTitle === 'LME' && dadCompanySetting.exempt && !dadCompanySetting.reportAsSubvendor) {
      this.error = true;
      this.errorMessage = 'Problem with ' + dadCompanySetting.dadConfig.exchangeNameInTitle + ' settings. Report as subvendor must be checked along with exempt option.';
      window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
      return;
    }
    if (dadCompanySetting.selfReport && dadCompanySetting.reportAsSubvendor) {
      this.error = true;
      this.errorMessage = 'Problem with ' + dadCompanySetting.dadConfig.exchangeNameInTitle + ' settings. You can not activate the report as subvendor and self-reporting options at the same time.';
      window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
      return;
    }
    this.customCompanyService.changeDadCompanySettings(dadCompanySetting, this.currentCompany.uniqid).subscribe({
      next: () => {
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        this.dadConfigSaved = true;
        this.successEdit = true;
      }, error: error => {
        if (error.status === 406) {
          this.successEdit = false;
          this.error = true;
          this.errorMessage = 'You can not change form style when the auto report is activated.';
        }
      }
    });
  }

  disableDadInput(dadConfigName: any) {
    if (this.isShowPage()) {
      return true;
    }
    return this.currentCompany.dadCompanySettings.filter(d => d.dadConfig.exchangeNameInTitle === dadConfigName).length === 0;
  }

  getNgModel(dadConfigName: any) {
    const dadCompanySettingsArray = this.currentCompany.dadCompanySettings.filter(s => s.dadConfig.exchangeNameInTitle === dadConfigName);
    if (dadCompanySettingsArray.length === 0) {
      return {} as any;
    }
    return dadCompanySettingsArray[0];
  }

  changeDadCompanySettings(dadConfig) {
    if (!this.changedDadSettings.includes(dadConfig)) {
      this.changedDadSettings.push(dadConfig);
    }
  }

  activateOrDeactivateAwsAccess() {
    if (this.currentCompany.awsPacketCounterAccess) {
      this.confirmationService.confirm({
        message: `Are you sure you want to deactivate AWS access for "${this.currentCompany.name}" ?`,
        accept: () => {
          this.companyService.putCompanyItem(this.currentCompany.id.toString(), {
            awsPacketCounterAccess: false,
            awsMqttPortsCountersActivationDate: null
          }).subscribe({
            next: () => {
              this.currentCompany['awsPacketCounterAccess'] = false;
              this.msgsAws = [];
              this.msgsAws.push({
                severity: 'error',
                summary: 'Access revoked',
                detail: `Access to AWS for "${this.currentCompany.name}" has been revoked.`
              });
              this.currentCompany.awsPacketCounterAccess = false;
            },
            error: () => {
              this.showErrorMessage('revoking');
            }
          });
        }
      });
    } else {
      this.confirmationService.confirm({
        message: `Are you sure you want to activate AWS access for"${this.currentCompany.name}" ?`,
        accept: () => {
          this.companyService.putCompanyItem(this.currentCompany.id.toString(), {awsPacketCounterAccess: true}).subscribe({
            next: () => {
              this.msgsAws = [];
              this.msgsAws.push({
                severity: 'success',
                summary: 'Access granted',
                detail: `Access to AWS for "${this.currentCompany.name}" has been granted.`
              });
              this.currentCompany.awsPacketCounterAccess = true;
            },
            error: () => {
              this.showErrorMessage('granting');
            }
          });
        }
      });
    }
  }

  private showErrorMessage(action: string) {
    this.msgsAws = [];
    this.msgsAws.push({
      severity: 'error',
      summary: 'Action failed',
      detail: `An error occurred while ${action} AWS access for "${this.currentCompany.name}"`
    });
  }


  deleteSelected(selectedId: number) {
    this.errorMessage = '';
    let selectedIndex: number;
    this.billingAccounts.forEach((element, index) => {
      if (element.id === selectedId) {
        selectedIndex = index;
      }
    });
    if (confirm(`This will delete this business unit: "${(this.billingAccounts)[selectedIndex].name}".If this account exists in Salesforce, it will be created again on Portal..`)) {
      this.customCompanyService.removeBillingAccount(selectedId).subscribe({
          next: () => {
            this.billingAccounts = this.billingAccounts.filter(ba => ba.id !== selectedId);
            this.successDelete = true;
            this.error = false;
          },
          error: error => {
            if (error.status === 400) {
              this.error = true;
              this.errorMessage = error.error.message;
            } else {
              this.redirectionToNotFound();
            }
          }
        }
      );
    }
  }

  canListUsers(): boolean {
    return this.roleService.userHasOneOfThoseRoles(this.getUserCurrent(), ['ROLE_MCKAY_SUPER', 'ROLE_MCKAY_ADMIN', 'ROLE_MCKAY_OPERATIONS', 'ROLE_MCKAY_DEVELOPER', 'ROLE_MCKAY_DAD_REPORTER', 'ROLE_MCKAY_FINANCE', ' ROLE_MCKAY_MAC_PERMISSIONER']);
  }

  canListLOAs(): boolean {
    return this.roleService.userHasOneOfThoseRoles(this.getUserCurrent(), ['ROLE_MCKAY_SUPER', 'ROLE_MCKAY_ADMIN', 'ROLE_MCKAY_OPERATIONS', 'ROLE_MCKAY_DEVELOPER']);
  }

  canListDadAdmin(): boolean {
    return this.roleService.userHasOneOfThoseRoles(this.getUserCurrent(), ['ROLE_MCKAY_SUPER', 'ROLE_MCKAY_ADMIN', 'ROLE_MCKAY_DAD_REPORTER']);
  }

  hasPermissionClientProfile(): boolean {
    return this.roleService.userHasOneOfThoseRoles(this.getUserCurrent(), ['ROLE_MCKAY_SUPER', 'ROLE_MCKAY_ADMIN', 'ROLE_MCKAY_DEVELOPER', 'ROLE_MCKAY_DAD_REPORTER']);
  }

  isAdminCompany(client): boolean {
    if (!client) {
      return false;
    }
    return client.uniqid === 'admin';
  }

  canListSchedules() {
    return this.getUserCurrent().permissions.canListSchedules;
  }

  hasQRDButton(): boolean {
    return this.hasPermissionClientProfile() && !this.isAdminCompany(this.currentCompany) && this.currentCompany.hasQrd;
  }

  hasQEDButton(): boolean {
    return this.hasPermissionClientProfile() && !this.isAdminCompany(this.currentCompany) && this.currentCompany.hasQed;
  }

  hasQFDButton(): boolean {
    return this.hasPermissionClientProfile() && !this.isAdminCompany(this.currentCompany) && this.currentCompany.hasQfd;
  }

  hasQHFButton(): boolean {
    return this.hasPermissionClientProfile() && !this.isAdminCompany(this.currentCompany) && this.currentCompany.hasQhf;
  }

  hasNewsButton(): boolean {
    return this.hasPermissionClientProfile() && !this.isAdminCompany(this.currentCompany) && this.currentCompany.hasNews;
  }

  hasBWButton(): boolean {
    return this.canListSchedules() && this.currentCompany.hasSchedule;
  }

  hasLOAsButton(): boolean {
    return this.currentCompany.hasLoa && this.canListLOAs();
  }

  hasDADAdminButton(): boolean {
    return this.currentCompany.dadInLastMonth && this.canListDadAdmin();
  }

  hasActionSection(): boolean {
    return this.hasQEDButton() || this.hasQRDButton() || this.hasQFDButton() || this.hasNewsButton() || this.hasQHFButton() || this.hasBWButton() || this.hasLOAsButton() || this.hasDADAdminButton();
  }

  private initDadConfigs() {
    this.clientUtilitiesService.getAllDadConfigs().subscribe(
      res => {
        this.dadConfigsNames = res
          .filter(dadConfig => !!dadConfig['exchangeNameInTitle'] && dadConfig['hasLinkedExchanges']);
      }
    );
  }

  private initSalesforceAccounts(uniqid: string) {
    this.clientUtilitiesService.getSalesforceAccounts(uniqid).subscribe(
      res => {
        this.salesforceAccounts = res.sort(function (a, b) {
          return a.name.localeCompare(b.name);
        });
        for (const account of res) {
          this.salesforceAccountsDropdown.push({
            label: account.name,
            value: account.uuid,
          });
        }
        this.accountDropdownPlaceHolder = 'Choose an account';
        if (this.companyUuid && this.salesforceAccountsDropdown.length > 0) {
          const selectedSalesForceAccount = this.salesforceAccountsDropdown.filter(account => account.value === this.companyUuid)[0];
          if (selectedSalesForceAccount) {
            this.selectedSalesforceAccountName = selectedSalesForceAccount.label;
            this.selectedSalesforceAccount = selectedSalesForceAccount.value;
          }
        }
      }
    );
  }

  private getCompanyAdminUsers() {
    if (!this.getUserCurrent().company) {
      return;
    }
    this.userService.getUserCollection(this.getUserCurrent().company.uniqid).subscribe(
      users => {
        this.companyAdminUsers = users;
      }
    );
  }

  private getClientByUniqid(companyUniqid) {
    this.companyService.getCompanyItem(companyUniqid).subscribe({
        next: client => {
          this.currentCompany = client[0];
          this.isTimeSyncOnly = this.currentCompany.timeSyncOnly;
          if (this.page === PageCrud.EDIT) {
            this.clientUsers = this.currentCompany.usersInfos;
            this.billingAccounts = this.currentCompany.billingAccounts;
          }
          this.successGetClientToShow();
        },
        error: () => {
          this.redirectionToNotFound();
        }
      }
    );
  }

  private successGetClientToShow() {
    this.companyUuid = this.currentCompany.sugarUuid;
    this.primaryContactName = this.currentCompany.primaryContactName;
    this.loading = false;
    if (this.salesforceAccountsDropdown.length > 0) {
      const selectedSalesForceAccount = this.salesforceAccountsDropdown.filter(account => account.value === this.companyUuid)[0];
      if (selectedSalesForceAccount) {
        this.selectedSalesforceAccount = selectedSalesForceAccount.value;
      }
    }
  }

  private addClient() {
    const selectedAccount = this.salesforceAccounts.filter(account => account.uuid === this.selectedSalesforceAccount)[0];
    this.newClientData['sugarShortname'] = selectedAccount.shortName;
    this.newClientData['sugarUuid'] = selectedAccount.uuid;
    this.newClientData['name'] = selectedAccount.name;
    this.newClientData['label'] = this.getAffiliation();
    this.primaryContact['email'] = this.primaryContactEmail;
    this.primaryContact['username'] = this.primaryContactEmail;
    this.primaryContact['name'] = this.primaryContactName;
    this.newClientData['primaryContact'] = this.primaryContact;
    this.newClientData['timeSyncOnly'] = this.isTimeSyncOnly;
    this.customCompanyService.addCompany(this.newClientData).subscribe({
        next: () => {
          this.successAdd = true;
          this.error = false;
          window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        },
        error: err => {
          this.errorAddClient(err);
          window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        }
      }
    );
  }

  private getAffiliation(): number {
    if (this.affiliationMckay && !this.affiliationQuincy) {
      return 1;
    }
    if (!this.affiliationMckay && this.affiliationQuincy) {
      return 2;
    }
    if (this.affiliationMckay && this.affiliationQuincy) {
      return 3;
    }
    if (this.affiliationVendor) {
      return 4;
    }
    return 0;
  }

  private updateCompanyInfo() {
    let hasChangedInformation = false;
    let hasChangedSalesforceAccount = false;
    if (this.selectedSalesforceAccount !== this.currentCompany.sugarUuid
      && this.salesforceAccounts.filter(account => account.uuid === this.selectedSalesforceAccount).length > 0
      && this.salesforceAccounts.filter(account => account.uuid === this.selectedSalesforceAccount)[0] !== null) {
      hasChangedInformation = true;
      hasChangedSalesforceAccount = true;
      const selectedAccount = this.salesforceAccounts.filter(account => account.uuid === this.selectedSalesforceAccount)[0];
      this.updateClient['sugarShortname'] = selectedAccount.shortName;
      this.updateClient['sugarUuid'] = selectedAccount.uuid;
      this.updateClient['name'] = selectedAccount.name;
    }
    if (this.isTimeSyncOnly !== this.currentCompany.timeSyncOnly) {
      hasChangedInformation = true;
      this.updateClient['timeSyncOnly'] = this.isTimeSyncOnly;
    }
    if (this.primaryContactName !== this.currentCompany.primaryContactName) {
      hasChangedInformation = true;
      this.updateClient['primaryContact'] = this.getPrimaryContact();
    }
    if (this.changedDadSettings.length > 0) {
      for (const dadCompanySetting of this.currentCompany.dadCompanySettings) {
        if (this.changedDadSettings.includes(dadCompanySetting.dadConfig.exchangeNameInTitle)) {
          this.editDadCompanySetting(dadCompanySetting);
        }
      }
      this.changedDadSettings = [];
      window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
      if (!hasChangedInformation) {
        return;
      }
    }
    if (!hasChangedInformation) {
      window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
      this.alertNoChange = true;
      this.successEdit = false;
      this.successAdd = false;
      this.error = false;
      return;
    }
    this.alertNoChange = false;
    this.companyService.putCompanyItem(String(this.currentCompany.id), this.updateClient).subscribe({
        next: res => {
          this.submitted = false;
          this.successEdit = true;
          this.currentCompany.primaryContactName = res.primaryContactName;
          this.currentCompany.timeSyncOnly = this.updateClient['timeSyncOnly'];
          if (hasChangedSalesforceAccount) {
            const selectedAccount = this.salesforceAccounts.filter(account => account.uuid === this.selectedSalesforceAccount)[0];
            this.currentCompany.sugarUuid = selectedAccount.uuid;
            this.currentCompany.name = selectedAccount.name;
          }
          window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
        }, error: error => {
          if (error.status === 401) {
            this.accessDenied = true;
            window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
          } else {
            this.redirectionToNotFound();
          }
        }
      }
    );
  }
}
